/**
 * Reseller-managed sender pool. After migration 000024:
 *   - The reseller (or direct customer) owns a pool of numbers.
 *   - Each pool entry goes draft → pending_review → approved (or
 *     auto-approves when the owner tenant has senders_auto_approve).
 *   - Approved entries get assigned to specific customer tenants.
 *   - The customer-facing /v1/senders becomes read-only: customers
 *     only see what's been assigned to them.
 *
 * Platform-admin endpoints (approval queue, suspend, auto-approve flag
 * toggle) live in usePlatformSenders.ts to keep concerns separated even
 * though the backend handler is shared.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '@/lib/api';

import type { RegistrationStatus, SenderType } from './useSenders';

export interface PoolSender {
  id: number;
  owner_tenant_id: number;
  assigned_to_tenant_id?: number;
  assigned_to_name?: string;
  type: SenderType;
  value: string;
  country_code: string;
  registration_status: RegistrationStatus | 'draft' | 'pending_review';
  use_case?: string;
  monthly_fee: number;
  submitted_at?: string;
  reviewed_at?: string;
  rejection_reason?: string;
  suspended_reason?: string;
  auto_approved: boolean;
  assigned_at?: string;
  created_at: string;
}

interface PoolListResponse {
  items: PoolSender[];
  total: number;
}

const POOL_KEY = ['admin', 'senders'] as const;

export function useAdminSendersList() {
  return useQuery({
    queryKey: POOL_KEY,
    queryFn: () => api.get<PoolListResponse>('/v1/admin/senders'),
    staleTime: 15_000,
  });
}

export interface CreatePoolInput {
  type: SenderType;
  value: string;
  country_code: string;
  use_case?: string;
}

export function useCreatePoolSender() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreatePoolInput) => api.post<PoolSender>('/v1/admin/senders', input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: POOL_KEY });
    },
  });
}

export function useUpdatePoolSender() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: number; input: CreatePoolInput }) =>
      api.patch<PoolSender>(`/v1/admin/senders/${id}`, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: POOL_KEY });
    },
  });
}

export function useDeletePoolSender() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.delete<void>(`/v1/admin/senders/${id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: POOL_KEY });
    },
  });
}

export function useSubmitPoolSender() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.post<PoolSender>(`/v1/admin/senders/${id}/submit`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: POOL_KEY });
    },
  });
}

export function useWithdrawPoolSender() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.post<PoolSender>(`/v1/admin/senders/${id}/withdraw`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: POOL_KEY });
    },
  });
}

export function useAssignPoolSender() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, customer_tenant_id }: { id: number; customer_tenant_id: number }) =>
      api.post<PoolSender>(`/v1/admin/senders/${id}/assign`, { customer_tenant_id }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: POOL_KEY });
    },
  });
}

export function useUnassignPoolSender() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.post<PoolSender>(`/v1/admin/senders/${id}/unassign`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: POOL_KEY });
    },
  });
}
